import { useEffect, useRef } from "react";
import type { Viewport } from "@archlang/scene/board";
import type { ViewportStore } from "./viewportStore";
import { createCameraHistory } from "./cameraHistory";

/**
 * Camera history — **⌃⌥← / ⌃⌥→** step back / forward through the viewports the
 * camera has come to REST at (browser-history semantics; see `cameraHistory.ts`).
 *
 * Rests are captured uniformly by watching the viewport store go quiet: every
 * settle — an auto-frame, a drift-back, a dial press, a spotlight restore, or a
 * manual pan — ends with the viewport idle, so one debounced sample per settle
 * records them all without threading a push call through each motion source.
 *
 * Stepping animates to the entry and is treated as a manual gesture (`onStep` →
 * suppress autopilot); the resulting settle is flagged so a replayed position
 * isn't re-recorded (no self-pollution) and doesn't truncate the forward stack.
 *
 * `e.code`-matched + capture phase (survives editor focus). Registered whenever
 * `active` (interactive, non-forced surface) — like the other explicit verbs, it
 * works whether or not smart-view autopilot is on.
 */
export interface CameraHistoryOpts {
  readonly active: boolean;
  readonly store: ViewportStore;
  readonly animateTo: (vp: Viewport) => void;
  readonly setViewportImmediate: (vp: Viewport) => void;
  /** Called on a step so the operator backs off (stepping is deliberate nav). */
  readonly onStep: () => void;
  readonly resetKey?: unknown;
}

// Long enough to clear an in-flight camera animation, so we sample the REST, not
// an intermediate frame.
const SETTLE_MS = 500;

function samePath(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

export function useCameraHistory(opts: CameraHistoryOpts): void {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const histRef = useRef(createCameraHistory());
  // Set true the moment we step, consumed by the next settle so a replayed
  // position isn't re-pushed.
  const navigatingRef = useRef(false);

  useEffect(() => {
    histRef.current.reset();
  }, [opts.resetKey]);

  // Debounced settle → push the rest viewport.
  useEffect(() => {
    if (!opts.active) return;
    const store = optsRef.current.store;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsub = store.subscribe(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (navigatingRef.current) {
          navigatingRef.current = false; // a replayed step — don't record it
          return;
        }
        histRef.current.push(store.get());
      }, SETTLE_MS);
    });
    return () => {
      unsub();
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.active]);

  // ⌃⌥←/→ step.
  useEffect(() => {
    if (!opts.active) return;
    const go = (vp: Viewport | null): void => {
      if (!vp) return;
      const o = optsRef.current;
      navigatingRef.current = true;
      o.onStep(); // treat as a manual gesture — suppress autopilot
      if (samePath(vp.focalPath, o.store.get().focalPath)) o.animateTo(vp);
      else o.setViewportImmediate(vp);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (!e.ctrlKey || !e.altKey || e.metaKey || e.shiftKey) return;
      if (e.code === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        go(histRef.current.back());
      } else if (e.code === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        go(histRef.current.forward());
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.active]);
}
